"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { useRef, useEffect } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Quote } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  author: "María García",
  quote: "Comida casera espectacular en un ambiente acogedor. Los platos están elaborados con ingredientes frescos y de calidad. El servicio es excelente y te hacen sentir como en casa. Volveré sin duda.",
  position: "Reseña de Google",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1539391/img/maria_garcia_avatar.png"
}, {
  id: 2,
  author: "José López",
  quote: "Experiencia gastronómica auténtica. La selección de vinos es excepcional y cada plato es una delicia. El personal es muy atento y conoce perfectamente la carta. Totalmente recomendable.",
  position: "Reseña de Google",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1539391/img/jose_lopez_avatar.png"
}, {
  id: 3,
  author: "Carmen Rodríguez",
  quote: "Uno de los mejores restaurantes de Puerto de la Cruz. La comida tiene ese toque casero que tanto se echa de menos. Ambiente familiar y precios justos para la calidad que ofrecen.",
  position: "Reseña de Google",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1539391/img/carmen_rodriguez_avatar.png"
}, {
  id: 4,
  author: "Antonio Martínez",
  quote: "Simplemente perfecto. Cada bocado es una celebración de la tradición canario-española. Los vinos tienen historia y el ambiente te transporta a una bodega auténtica. Una joya escondida.",
  position: "Reseña de Google",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1539391/img/antonio_martinez_avatar.png"
}, {
  id: 5,
  author: "Isabel Fernández",
  quote: "Calidad excepcional en todos los aspectos. Desde el momento en que entras, sientes la calidez del lugar. La comida es deliciosa, los vinos perfectos y el servicio impecable. Un lugar al que siempre quieres volver.",
  position: "Reseña de Google",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1539391/img/isabel_fernandez_avatar.png"
}];
export default function Testimonios() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const [api, setApi] = React.useState<any>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [isHovered, setIsHovered] = React.useState(false);
  useEffect(() => {
    if (!api) return;
    const startAutoScroll = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      timerRef.current = setInterval(() => {
        if (!isHovered) {
          api.scrollNext();
        }
      }, 3500);
    };
    startAutoScroll();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [api, isHovered]);
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  return <section id="testimonios" ref={sectionRef} className="dark bg-background text-foreground py-24 px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-background to-secondary/20 animate-pulse" style={{
      animationDuration: "8s"
    }} />

      <div className="container mx-auto max-w-7xl relative z-10">
        <div className={`text-center mb-16 transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-4">
            Testimonios
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Descubre lo que nuestros clientes dicen sobre su experiencia en Bodega Julian
          </p>
        </div>

        <div className={`transition-all duration-[var(--duration-slow)] delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <Carousel setApi={setApi} opts={{
          align: "start",
          loop: true,
          dragFree: true
        }} className="w-full">
            <CarouselContent className="-ml-4">
              {testimonials.map((testimonial, i) => <CarouselItem key={testimonial.id} data-index={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <Card className="card h-full bg-secondary-active/90 border border-white/5 backdrop-blur-sm">
                    <CardContent className="p-8 flex flex-col h-full">
                      <Quote className="text-foreground-dark w-10 h-10 mb-6 opacity-60" />
                      
                      <blockquote className="text-foreground text-lg leading-relaxed mb-8 flex-grow font-sans">
                        "{testimonial.quote}"
                      </blockquote>

                      <div className="flex items-center gap-4 mt-auto">
                        <SafeHydrate><Avatar className="w-14 h-14 border-2 border-primary/30">
                          <AvatarImage src={testimonial.image} alt={testimonial.author} />
                          <AvatarFallback className="bg-primary text-foreground-primary font-semibold">
                            {testimonial.author.split(" ").map(n => n[0]).join("")}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>
                        
                        <div>
                          <p className="font-semibold text-foreground text-base">
                            {testimonial.author}
                          </p>
                          <p className="text-muted-foreground text-sm">
                            {testimonial.position}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>)}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </section>;
}